import { getSupabaseClient } from './supabase.client.js';

/**
 * Repositorio de `payroll_config` (employee-payroll-extensions D2/E1). Full
 * CRUD desde el navegador vía RLS admin-only (`payroll_config_admin_all`,
 * migración 23) — mismo patrón que `feriados.repository.js`.
 *
 * Thin, SIN validar (la validación vive en el service, slice E1, mismo
 * criterio que `admin-empleados.service.js`). Singleton (`id boolean
 * primary key default true check (id)`), sin fila sembrada a propósito
 * (AD11): la lectura AUTORITATIVA para liquidar vive server-side en
 * `api/liquidacion.js` (fase 1b), NUNCA acá — esto es sólo el editor admin.
 *
 * Columnas SIEMPRE explícitas (nunca `select('*')`).
 */

// S4 (AD17, design.md): `periodo_por_defecto` agregada al mismo singleton
// por la migración 28, junto a `adelantos` — nullable, AUSENTE = 'mes'
// (leído así por admin-payroll-config.service.js). NO usa el patrón "sin
// fila sembrada = 503" del tope mensual: es una preferencia de pantalla, no
// un dato monetario.
const PAYROLL_CONFIG_COLUMNS = 'id, tope_descuento_mensual, updated_at, updated_by, periodo_por_defecto';

/**
 * Re-lanza un error de Supabase preservando el código de Postgres (mismo
 * patrón que el resto de los repositorios de este slice).
 * @param {{ message: string, code?: string }} error
 * @returns {never}
 */
function rethrow(error) {
    const err = new Error(error.message);
    if (error.code) err.code = error.code;
    throw err;
}

/**
 * Lee la fila única de `payroll_config`, o `null` si no está configurada
 * (AD11) o Supabase no está disponible (misma degradación de lectura del
 * resto del panel). `.maybeSingle()`: sin fila es un estado esperado, no
 * un `PGRST116` que haya que atrapar.
 * @returns {Promise<{ id: true, tope_descuento_mensual: number, updated_at: string, updated_by: string|null } | null>}
 * @throws {Error} si Supabase responde con error
 */
export async function getPayrollConfig() {
    const client = getSupabaseClient();
    if (!client) return null;

    const { data, error } = await client.from('payroll_config').select(PAYROLL_CONFIG_COLUMNS).maybeSingle();
    if (error) rethrow(error);
    return data ?? null;
}

/**
 * Crea o reemplaza la fila única de `payroll_config` (`upsert` sobre `id:
 * true`). El caller (servicio E1) valida `topeDescuentoMensual` ANTES de
 * llamar acá — el CHECK de la base es el último resguardo, no el primero.
 * @param {{ topeDescuentoMensual: number, updatedBy: string|null }} params
 * @returns {Promise<{ id: true, tope_descuento_mensual: number, updated_at: string, updated_by: string|null }>}
 * @throws {Error} si Supabase no está configurado o la query falla (`error.code` preservado, p.ej. `23514`)
 */
export async function savePayrollConfig({ topeDescuentoMensual, updatedBy }) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Configuración de liquidación no disponible');

    const { data, error } = await client
        .from('payroll_config')
        .upsert(
            {
                id: true,
                tope_descuento_mensual: topeDescuentoMensual,
                updated_at: new Date().toISOString(),
                updated_by: updatedBy,
            },
            { onConflict: 'id' },
        )
        .select(PAYROLL_CONFIG_COLUMNS)
        .single();

    if (error) rethrow(error);
    return data;
}

/**
 * Código propio (no de Postgres) del error de `savePeriodoPorDefecto` cuando
 * el UPDATE no encontró la fila singleton. `admin-payroll-config.service.js`
 * lo mapea (por este mismo string) a un `{ ok: false, error }` con el mensaje.
 */
const PAYROLL_CONFIG_MISSING = 'PAYROLL_CONFIG_MISSING';

/**
 * Guarda SOLO `periodo_por_defecto` con un UPDATE de la fila singleton
 * existente (`.eq('id', true)`) — NUNCA toca `tope_descuento_mensual`. El
 * caller (servicio, S6) valida `periodoPorDefecto` ANTES de llamar acá — el
 * CHECK de la base (`in ('mes','quincena','semana')`, migración 28) es el
 * último resguardo.
 *
 * Por qué UPDATE y no upsert (bug 2026-09-26, HTTP 400 en cada guardado): un
 * upsert parcial sin `tope_descuento_mensual` falla SIEMPRE, exista o no la
 * fila, porque Postgres evalúa el NOT NULL de esa columna (sin default,
 * migración 23) sobre la fila propuesta del INSERT ANTES de resolver el
 * `ON CONFLICT`. La nota anterior asumía que solo fallaba sin fila.
 *
 * Sin fila: la migración 23 NO siembra la fila a propósito (AD11); la crea
 * el primer guardado del tope mensual (`savePayrollConfig`). Si el UPDATE no
 * afecta ninguna fila (tope nunca cargado — o la RLS la ocultó, que para un
 * UPDATE también da cero filas en vez de un 42501) se lanza un error
 * específico con `code: PAYROLL_CONFIG_MISSING`, nunca un éxito silencioso.
 * @param {{ periodoPorDefecto: 'mes'|'quincena'|'semana'|null, updatedBy: string|null }} params
 * @returns {Promise<{ id: true, periodo_por_defecto: string|null, updated_at: string, updated_by: string|null }>}
 * @throws {Error} si Supabase no está configurado, la query falla (`error.code` preservado, p.ej. el CHECK de la base) o no hay fila (`code: PAYROLL_CONFIG_MISSING`)
 */
export async function savePeriodoPorDefecto({ periodoPorDefecto, updatedBy }) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Configuración de liquidación no disponible');

    const { data, error } = await client
        .from('payroll_config')
        .update({
            periodo_por_defecto: periodoPorDefecto,
            updated_at: new Date().toISOString(),
            updated_by: updatedBy,
        })
        .eq('id', true)
        .select(PAYROLL_CONFIG_COLUMNS);

    if (error) rethrow(error);
    if (!data || data.length === 0) {
        const err = new Error(
            'Todavía no hay configuración de liquidación: cargá primero el tope mensual de descuento y después el período por defecto.',
        );
        err.code = PAYROLL_CONFIG_MISSING;
        throw err;
    }
    return data[0];
}
